import { create, type StateCreator, type StoreApi, type UseBoundStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { ipcSync, type IpcSyncOptions } from './middleware';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Configuration for createSyncStore.
 */
export interface SyncStoreConfig<TState, TActions> {
  /**
   * IPC sync channel name. Stores sharing a channel synchronize.
   */
  channel: string;

  /**
   * Which state keys to sync across windows.
   * Defaults to ALL state keys (actions are never synced).
   */
  syncKeys?: (keyof TState & string)[];

  /**
   * Throttle interval in ms for IPC broadcasts. Defaults to 50.
   */
  throttleMs?: number;

  /**
   * Initial state values (data only, no actions).
   */
  state: TState;

  /**
   * Action creators. Receives `set` and `get` typed to the full store.
   */
  actions: (
    set: (
      partial: Partial<TState & TActions> | ((state: TState & TActions) => Partial<TState & TActions>),
      replace?: boolean
    ) => void,
    get: () => TState & TActions
  ) => TActions;

  /**
   * Optional: additional ipcSync options (onSend, onReceive, origin, adapter).
   */
  syncOptions?: Omit<IpcSyncOptions<TState & TActions>, 'channel' | 'syncKeys' | 'throttleMs'>;
}

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * Create a synced zustand store with a clean separation of state and actions.
 *
 * Define once in a shared module, import into any renderer app.
 * Each app gets its own store instance (separate processes), but
 * the ipcSync middleware keeps synced keys in lockstep via IPC.
 *
 * Comes with `subscribeWithSelector` baked in for granular subscriptions.
 *
 * Returns the zustand hook directly — use it in components as a hook,
 * or call `.getState()`, `.setState()`, `.subscribe()` on it outside React.
 *
 * @example
 * ```ts
 * // shared/stores/appStore.ts
 * export const useAppStore = createSyncStore({
 *   channel: 'app',
 *   syncKeys: ['theme', 'locale', 'user'],
 *   throttleMs: 50,
 *
 *   state: {
 *     theme: 'light' as 'light' | 'dark',
 *     locale: 'en',
 *     user: null as { name: string } | null,
 *     sidebarOpen: false,
 *   },
 *
 *   actions: (set, get) => ({
 *     toggleTheme: () => set({ theme: get().theme === 'light' ? 'dark' : 'light' }),
 *     setLocale: (locale: string) => set({ locale }),
 *     login: (name: string) => set({ user: { name } }),
 *     logout: () => set({ user: null }),
 *     toggleSidebar: () => set(s => ({ sidebarOpen: !s.sidebarOpen })),
 *   }),
 * });
 *
 * // React component:
 * const theme = useAppStore(s => s.theme);
 * const toggleTheme = useAppStore(s => s.toggleTheme);
 *
 * // Outside React:
 * useAppStore.subscribe(
 *   s => s.theme,
 *   (theme) => console.log('theme changed:', theme),
 * );
 * useAppStore.getState().login('Bob');
 * ```
 */
export function createSyncStore<
  TState extends Record<string, unknown>,
  TActions extends Record<string, (...args: never[]) => unknown>
>(config: SyncStoreConfig<TState, TActions>): UseBoundStore<StoreApi<TState & TActions>> {
  const { channel, syncKeys, throttleMs = 50, state: initialState, actions: actionCreator, syncOptions = {} } = config;

  type Store = TState & TActions;

  return create<Store>()(
    subscribeWithSelector(
      ipcSync<Store>(
        {
          channel,
          syncKeys: syncKeys as (keyof Store & string)[] | undefined,
          throttleMs,
          ...syncOptions,
        },
        (set, get) => {
          const actions = actionCreator(
            set as Parameters<SyncStoreConfig<TState, TActions>['actions']>[0],
            get as () => Store
          );

          return {
            ...initialState,
            ...actions,
          } as Store;
        }
      ) as StateCreator<Store, [['zustand/subscribeWithSelector', never]], []>
    )
  );
}
